import { Component, Fragment } from 'react';
import { IPastVitals, Vital } from '../api/fetchPastVitalsByPtOid';
import RightPopUp from '../components/common/RightPopUp';
import groupBy from 'lodash/groupBy';
import MediaViewer, { getColorClass } from '../components/common/MediaViewer';
import { captureError } from './captureError';
import { track } from '../tracking';
import { PulseLoader } from '../components/common/Icons';
import VitalFileDisplay from './VitalFileDisplay';
import fetchAllMedicalRecordsInADoc, { FilesEntity } from '../api/fetchAllMedicalRecordsInADoc';
import moment from 'moment';
import VitalsGraph from '../components/VitalsGraph';
import {
    InterpretationTooltip,
    RangeTooltip,
} from '../components/prescriptionPad/MedicalRecordsComponent';
import { smoothScrollToItemWithDelay } from '../store/utils';

interface Props {
    fData: IPastVitals[];
    selectedVitalId: string;
    closePopUp: () => void;
    patientId: string;
}

interface State {
    currValue?: {
        vital_id?: string;
        document_id?: string;
    };
    fetchState: 'loading' | 'success' | 'error';
    pageNo: number | undefined;
    files?: FilesEntity[] | undefined | null;
    documentIndex?: number;
    currVital: IPastVitals | undefined;
}
class VitalTrendPopUp extends Component<Props, State> {
    state: State = {
        currValue: {
            vital_id: this.props.fData.find((v) => v.id === this.props.selectedVitalId)?.values[0]
                .vital_id,
            document_id: this.props.fData.find((v) => v.id === this.props.selectedVitalId)
                ?.values[0].document_id,
        },
        currVital: this.props.fData.find((v) => v.id === this.props.selectedVitalId),
        fetchState: 'loading',
        files: undefined,
        pageNo: undefined,
        documentIndex: undefined,
    };

    componentDidMount(): void {
        this.handleValueClick(
            this.props.fData.find((v) => v.id === this.props.selectedVitalId)?.values[0],
        );
    }

    setCurrValue = (vital: Vital): void =>
        this.setState((prev) => {
            return {
                ...prev,
                currValue: {
                    ...prev['currValue'],
                    vital_id: vital.vital_id,
                    document_id: vital.document_id,
                },
            };
        });

    onRetry = (currVital: IPastVitals | undefined): void => {
        const { currValue } = this.state;

        if (currVital && currValue) {
            const vital = currVital.values.find((v) => v.vital_id === currValue.vital_id);
            this.handleValueClick(vital);
        }
    };

    handleValueClick = (vital?: Vital): void => {
        if (!vital) {
            return;
        }

        this.setCurrValue(vital);
        smoothScrollToItemWithDelay(`vital-trend-popup-reading-${vital.vital_id}`);

        if (!vital.document_id) {
            this.setState({
                fetchState: 'success',
                documentIndex: undefined,
                pageNo: undefined,
                files: undefined,
            });
            return;
        }

        this.setState({ fetchState: 'loading' });

        fetchAllMedicalRecordsInADoc(
            this.state.currVital?.pid ?? this.props.patientId,
            vital.document_id,
            this.props.selectedVitalId,
        )
            .then((res) => {
                track('FETCH_MR_FOR_VITAL', { status: 'success' });
                this.setState({
                    fetchState: 'success',
                    documentIndex: res.smart_report?.verified
                        ? res.smart_report?.verified[0].file_index
                        : undefined,
                    pageNo: res.smart_report?.verified
                        ? res.smart_report?.verified[0].page_num
                        : undefined,
                    files: res.files,
                });
            })
            .catch((err) => {
                track('FETCH_MR_FOR_VITAL', { status: 'failed' });
                console.error(err);
                captureError(err);
                this.setState({
                    fetchState: 'error',
                    documentIndex: undefined,
                    pageNo: undefined,
                    files: undefined,
                });
            });
    };

    render() {
        const { closePopUp } = this.props;
        const { currValue, fetchState, documentIndex, files } = this.state;
        const { currVital } = this.state;
        const groupedCurrVitals = groupBy(currVital?.values, 'isoDateObject');
        const dates = Object.keys(groupedCurrVitals);

        const vitalValues = currVital?.values.slice().reverse();
        const graphData = vitalValues?.map((v) => {
            return {
                x: moment(v.isoDateObject).format("MMM'YY"),
                y: v.value.aValue,
                isOutOfRange: getColorClass(v.result, '', '') ? true : false,
                v: v,
            };
        });
        const vitalDates = vitalValues?.map((v) => moment(v.isoDateObject).format('DD/MM/YYYY'));

        const isGraphPossible =
            vitalDates &&
            vitalDates.length !== 0 &&
            graphData &&
            graphData.length !== 0 &&
            !graphData?.find((v) => isNaN(v.y)) &&
            !currVital?.values.find((c) => !c.isoDateObject);

        return (
            <RightPopUp closeMe={closePopUp} width="72%">
                <div className="flex flex-col h-full flex-1 overflow-scroll">
                    <div className="flex items-center justify-between px-16 py-12 border-b border-darwin-neutral-100 bg-white h-68">
                        <div className="flex items-center gap-12">
                            <div
                                onClick={closePopUp}
                                className="flex items-center justify-center w-16 h-16 cursor-pointer"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 448 512"
                                    className="fill-darwin-neutral-1000 w-16 h-16"
                                >
                                    <path d="M447.1 256c0 13.25-10.76 24.01-24.01 24.01H83.9l132.7 126.6c9.625 9.156 9.969 24.41 .8125 33.94c-9.156 9.594-24.34 9.938-33.94 .8125l-176-168C2.695 268.9 .0078 262.6 .0078 256S2.695 243.2 7.445 238.6l176-168C193 61.51 208.2 61.85 217.4 71.45c9.156 9.5 8.812 24.75-.8125 33.94l-132.7 126.6h340.1C437.2 232 447.1 242.8 447.1 256z" />
                                </svg>
                            </div>
                            <p className="font-400 text-20 capitalize">{currVital?.name}</p>
                            <div className="rounded-t-4 text-neutral-black font-700 "></div>
                        </div>
                    </div>
                    <div className="flex flex-col w-full h-full overflow-hidden">
                        {isGraphPossible && graphData && (
                            <div
                                className={`bg-darwin-neutral_new-50 ${
                                    files &&
                                    files.length !== 0 &&
                                    documentIndex !== undefined &&
                                    documentIndex >= 0
                                        ? ''
                                        : 'border-b border-darwin-neutral_new-200'
                                }`}
                            >
                                <VitalsGraph
                                    graphData={graphData}
                                    unit={currVital?.unit}
                                    handleValueClick={this.handleValueClick}
                                />
                            </div>
                        )}
                        <div className="flex bg-darwin-neutral_new-50 h-full overflow-hidden">
                            <div className="flex flex-col pt-24 gap-12">
                                <p className="text-16 px-24 font-700">
                                    Readings
                                    {currVital?.unit ? ` (${currVital?.unit})` : ''}
                                </p>
                                <div className="flex px-24 flex-col gap-12 overflow-scroll hide-scrollbar pb-36">
                                    {dates.map((v) => (
                                        <Fragment>
                                            {groupedCurrVitals[v].map((vital) => {
                                                const colorClass = getColorClass(
                                                    vital.result,
                                                    '',
                                                    '',
                                                );

                                                return (
                                                    <div
                                                        className="flex items-center gap-4"
                                                        id={`vital-trend-popup-reading-${vital.vital_id}`}
                                                    >
                                                        <div className="flex items-center gap-16 justify-between">
                                                            <div className="flex flex-shrink-0 flex-col">
                                                                <p className="font-700 text-14">
                                                                    {new Date(
                                                                        vital.isoDateObject,
                                                                    ).getDate()}
                                                                </p>
                                                                <p className="uppercase text-10">
                                                                    {new Date(
                                                                        vital.isoDateObject,
                                                                    ).toLocaleString('default', {
                                                                        month: 'short',
                                                                    })}
                                                                    '
                                                                    {new Date(
                                                                        vital.isoDateObject,
                                                                    ).toLocaleString('default', {
                                                                        year: '2-digit',
                                                                    })}
                                                                </p>
                                                            </div>
                                                            <div className="flex items-center gap-4">
                                                                <div
                                                                    className={`px-16 cursor-pointer flex-shrink-0 py-12 rounded-8 w-253 flex items-center space-x-8 bg-white ${
                                                                        currValue?.vital_id ===
                                                                            vital.vital_id &&
                                                                        'border border-darwin-accent-bright-blue-800'
                                                                    }`}
                                                                    onClick={() =>
                                                                        this.handleValueClick(vital)
                                                                    }
                                                                >
                                                                    <RangeTooltip vital={vital}>
                                                                        <p
                                                                            className={`font-400 text-13 cursor-pointer ${colorClass}`}
                                                                        >
                                                                            {vital.value.aValue}{' '}
                                                                            {vital.value.aUnit}
                                                                        </p>
                                                                    </RangeTooltip>
                                                                    <InterpretationTooltip
                                                                        vital={vital}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {currValue?.vital_id === vital.vital_id && (
                                                            <svg
                                                                width="6"
                                                                height="9"
                                                                viewBox="0 0 6 9"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    d="M4.99609 4.70898L1.74609 7.95898C1.51758 8.1875 1.16211 8.26367 0.857422 8.13672C0.552734 8.00977 0.349609 7.70508 0.349609 7.375V0.875C0.349609 0.544922 0.552734 0.265625 0.857422 0.138672C1.16211 0.0117188 1.51758 0.0878906 1.74609 0.316406L4.99609 3.56641C5.32617 3.87109 5.32617 4.4043 4.99609 4.70898Z"
                                                                    fill="#215FFF"
                                                                />
                                                            </svg>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </Fragment>
                                    ))}
                                </div>
                            </div>
                            <div
                                className={`flex ${
                                    files &&
                                    files.length !== 0 &&
                                    documentIndex !== undefined &&
                                    documentIndex >= 0
                                        ? ''
                                        : 'border-l border-darwin-neutral_new-200'
                                } justify-center items-center flex-col ${
                                    isGraphPossible ? 'mx-24' : 'm-24'
                                } w-full`}
                            >
                                {currValue?.document_id ? (
                                    fetchState === 'loading' ? (
                                        <div className="flex flex-col items-center justify-center space-y-72">
                                            <PulseLoader />
                                            <p className="text-24 text-neutral-black ml-12">
                                                Fetching Files
                                            </p>
                                        </div>
                                    ) : fetchState === 'error' ? (
                                        <VitalFileDisplay
                                            text="Error occured fetching your record"
                                            onRetry={() => this.onRetry(currVital)}
                                        />
                                    ) : files &&
                                      files.length !== 0 &&
                                      documentIndex !== undefined &&
                                      documentIndex >= 0 ? (
                                        <MediaViewer
                                            files={files}
                                            selectedIndex={documentIndex ?? 0}
                                            mediaViewerClassName="flex flex-col h-full w-full"
                                        />
                                    ) : (
                                        <VitalFileDisplay text="Could not find your record" />
                                    )
                                ) : (
                                    <VitalFileDisplay
                                        icon={
                                            <svg
                                                className="fill-darwin-neutral-400"
                                                viewBox="0 0 448 512"
                                                width="100"
                                                height="100"
                                            >
                                                <path d="M433.9 98L350 14.1C341 5.1 328.8 0 316.1 0h-204c-26.5.1-48 21.6-48 48.1V288H8c-4.4 0-8 3.6-8 8v16c0 4.4 3.6 8 8 8h126.1l34.7 69.5c2.9 5.9 11.4 5.9 14.3 0L240 275.8l22.1 44.2H352c8.8 0 16-7.2 16-16s-7.2-16-16-16h-70.1l-34.7-69.5c-2.9-5.9-11.4-5.9-14.3 0L176 332.2l-19.9-39.8c-1.4-2.7-4.1-4.4-7.2-4.4H96V48.1c0-8.8 7.3-16.1 16.1-16.1h176v104.1c0 13.3 10.7 23.9 24 23.9h104v304c0 8.8-7.2 16-16 16h-288c-8.8 0-16.1-7.2-16.1-16V352H64.1v112c0 26.5 21.5 48 48 48H400c26.5 0 48-21.5 48-48V132c0-12.7-5.1-25-14.1-34zM320 128.1V32.6c2.8.7 5.3 2.1 7.4 4.2l83.9 83.9c2.1 2.1 3.5 4.6 4.2 7.4H320z"></path>
                                            </svg>
                                        }
                                        text="This reading is added manually"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </RightPopUp>
        );
    }
}

export default VitalTrendPopUp;
